import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';

type Mode = 'electric' | 'thermal';
type MissingPowerField = 'power' | 'flow' | 'deltaT';
type MissingCostField = 'energy' | 'hours' | 'price' | 'cost';

type VoltageSystem = 'DC' | 'AC1' | 'AC3';

const normalizeNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value: number, digits = 2): string =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : Math.min(digits, 4),
    maximumFractionDigits: digits
  }).format(value);

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewEnergieKosten } = useAuth();
  const [mode, setMode] = useState<Mode>('thermal');
  const [missingPower, setMissingPower] = useState<MissingPowerField>('power');
  const [missingCost, setMissingCost] = useState<MissingCostField>('cost');

  const [voltageSystem, setVoltageSystem] = useState<VoltageSystem>('AC3');
  const [voltage, setVoltage] = useState('400');
  const [current, setCurrent] = useState('');
  const [cosPhi, setCosPhi] = useState('0.9');

  const [flow, setFlow] = useState('2.0');
  const [deltaT, setDeltaT] = useState('5');
  const [power, setPower] = useState('');

  const [energy, setEnergy] = useState('');
  const [hours, setHours] = useState('8');
  const [price, setPrice] = useState('0.35');
  const [cost, setCost] = useState('');

  const electricPowerKw = useMemo(() => {
    if (mode !== 'electric') return null;
    const u = normalizeNumber(voltage);
    const i = normalizeNumber(current);
    const cos = normalizeNumber(cosPhi) ?? 1;
    if (u === null || i === null) return null;
    if (voltageSystem === 'DC') return u * i / 1000;
    if (voltageSystem === 'AC1') return u * i * cos / 1000;
    return Math.sqrt(3) * u * i * cos / 1000;
  }, [mode, voltage, current, cosPhi, voltageSystem]);

  const thermalTargetResult = useMemo(() => {
    if (mode !== 'thermal') return null;
    const flowNum = normalizeNumber(flow);
    const deltaNum = normalizeNumber(deltaT);
    const powerNum = normalizeNumber(power);

    const calcPower = () => {
      if (flowNum === null || deltaNum === null) return null;
      return 1.163 * flowNum * deltaNum;
    };

    switch (missingPower) {
      case 'power':
        return { value: calcPower(), unit: 'kW', label: 'Leistung' };
      case 'flow':
        if (powerNum === null || deltaNum === null || deltaNum === 0) return null;
        return { value: powerNum / (1.163 * deltaNum), unit: 'm3/h', label: 'Durchfluss' };
      case 'deltaT':
        if (powerNum === null || flowNum === null || flowNum === 0) return null;
        return { value: powerNum / (1.163 * flowNum), unit: 'K', label: 'Delta T' };
      default:
        return null;
    }
  }, [mode, flow, deltaT, power, missingPower]);

  const thermalPowerKw = useMemo(() => {
    if (mode !== 'thermal') return null;
    const flowNum = normalizeNumber(flow);
    const deltaNum = normalizeNumber(deltaT);
    const powerNum = normalizeNumber(power);
    if (flowNum !== null && deltaNum !== null) return 1.163 * flowNum * deltaNum;
    return powerNum;
  }, [mode, flow, deltaT, power]);

  const costResult = useMemo(() => {
    const energyNum = normalizeNumber(energy);
    const hoursNum = normalizeNumber(hours);
    const priceNum = normalizeNumber(price);
    const costNum = normalizeNumber(cost);

    const powerForEnergy = () => (mode === 'electric' ? electricPowerKw : thermalPowerKw);

    const calcEnergy = () => {
      const p = powerForEnergy();
      if (p === null || hoursNum === null) return null;
      return p * hoursNum;
    };

    switch (missingCost) {
      case 'energy':
        return calcEnergy();
      case 'hours':
        if (energyNum === null) return null;
        const p = powerForEnergy();
        if (p === null || p === 0) return null;
        return energyNum / p;
      case 'price':
        if (costNum === null || energyNum === null || energyNum === 0) return null;
        return costNum / energyNum;
      case 'cost':
        const e = energyNum ?? calcEnergy();
        if (e === null || priceNum === null) return null;
        return e * priceNum;
      default:
        return null;
    }
  }, [energy, hours, price, cost, missingCost, mode, electricPowerKw, thermalPowerKw]);

  const powerHelper = mode === 'electric'
    ? 'Berechnet aus U, I und cos phi. Ergebnis in kW.'
    : 'Wasser-Formel: P = 1.163 * Durchfluss (m3/h) * Delta T (K).';

  const powerDisplay = useMemo(() => {
    if (mode === 'electric') {
      return { label: 'Leistung', unit: 'kW', value: electricPowerKw };
    }
    if (thermalTargetResult) {
      return { label: thermalTargetResult.label, unit: thermalTargetResult.unit, value: thermalTargetResult.value };
    }
    return { label: 'Zielgroesse', unit: '', value: null };
  }, [mode, electricPowerKw, thermalTargetResult]);

  const costPowerKw = useMemo(() => (mode === 'electric' ? electricPowerKw : thermalPowerKw), [mode, electricPowerKw, thermalPowerKw]);

  const detailSteps = useMemo(() => {
    const steps: string[] = [];
    if (mode === 'electric') {
      const u = normalizeNumber(voltage);
      const i = normalizeNumber(current);
      const cos = normalizeNumber(cosPhi) ?? 1;
      if (u !== null && i !== null && electricPowerKw !== null) {
        const baseFormula = voltageSystem === 'DC'
          ? `P = U * I = ${formatNumber(u, 3)} * ${formatNumber(i, 3)}`
          : voltageSystem === 'AC1'
            ? `P = U * I * cosφ = ${formatNumber(u, 3)} * ${formatNumber(i, 3)} * ${formatNumber(cos, 3)}`
            : `P = √3 * U * I * cosφ = 1,732 * ${formatNumber(u, 3)} * ${formatNumber(i, 3)} * ${formatNumber(cos, 3)}`;
        steps.push(`Leistungsberechnung: ${baseFormula} = ${formatNumber(electricPowerKw, 3)} kW`);
      }
    } else {
      const flowNum = normalizeNumber(flow);
      const deltaNum = normalizeNumber(deltaT);
      const powerNum = normalizeNumber(power);
      if (missingPower === 'power' && flowNum !== null && deltaNum !== null && thermalTargetResult !== null && thermalTargetResult.value !== null) {
        steps.push(`Leistung: P = 1,163 * Vdot * ΔT = 1,163 * ${formatNumber(flowNum, 3)} * ${formatNumber(deltaNum, 3)} = ${formatNumber(thermalTargetResult.value, 3)} kW`);
      }
      if (missingPower === 'flow' && powerNum !== null && deltaNum !== null && thermalTargetResult !== null && thermalTargetResult.value !== null) {
        steps.push(`Durchfluss: Vdot = P / (1,163 * ΔT) = ${formatNumber(powerNum, 3)} / (1,163 * ${formatNumber(deltaNum, 3)}) = ${formatNumber(thermalTargetResult.value, 3)} m3/h`);
      }
      if (missingPower === 'deltaT' && powerNum !== null && flowNum !== null && thermalTargetResult !== null && thermalTargetResult.value !== null) {
        steps.push(`Delta T: ΔT = P / (1,163 * Vdot) = ${formatNumber(powerNum, 3)} / (1,163 * ${formatNumber(flowNum, 3)}) = ${formatNumber(thermalTargetResult.value, 3)} K`);
      }
    }

    const priceNum = normalizeNumber(price);
    const hoursNum = normalizeNumber(hours);
    const energyNum = normalizeNumber(energy);
    if (priceNum !== null && costResult !== null) {
      if (missingCost === 'cost') {
        steps.push(`Kosten: K = E * Preis mit E = P * h. Ergebnis = ${formatNumber(costResult, 3)} EUR`);
      }
      if (missingCost === 'energy') {
        steps.push(`Energie: E = P * h = ${formatNumber(costResult, 3)} kWh`);
      }
      if (missingCost === 'hours' && energyNum !== null) {
        steps.push(`Zeit: h = E / P = ${formatNumber(costResult, 3)} h`);
      }
      if (missingCost === 'price' && hoursNum !== null) {
        steps.push(`Preis: Preis = Kosten / Energie = ${formatNumber(costResult, 3)} EUR/kWh`);
      }
    }
    return steps;
  }, [
    mode,
    voltage,
    current,
    cosPhi,
    voltageSystem,
    electricPowerKw,
    flow,
    deltaT,
    power,
    missingPower,
    thermalTargetResult,
    price,
    costResult,
    missingCost,
    energy,
    hours
  ]);

  if (!canViewEnergieKosten) {
    return (
      <UnitPageShell title="Energie-Kosten">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 U10 - Energie-Kosten Rechner">
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' } }}>
        <Card sx={getCardSx(darkMode)}>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h5" fontWeight={700}>Energie-Kosten</Typography>
                  <Tooltip title="Bidirektionaler Rechner fuer elektrische sowie thermische Leistung, Verbrauch und Kosten.">
                    <IconButton size="small" aria-label="Info Energie-Kosten"><InfoOutlinedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Berechnet Leistung, Verbrauch und Kosten fuer Strom, Waerme oder Kaelte. Rueckwaerts-Berechnung moeglich.
                </Typography>
              </Stack>

              <FormControl fullWidth>
                <InputLabel id="mode-select">Modus</InputLabel>
                <Select labelId="mode-select" label="Modus" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
                  <MenuItem value="thermal">Waerme / Kaelte (Durchfluss)</MenuItem>
                  <MenuItem value="electric">Elektrische Leistung</MenuItem>
                </Select>
              </FormControl>

              {mode === 'electric' ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel id="voltage-system">System</InputLabel>
                      <Select
                        labelId="voltage-system"
                        label="System"
                        value={voltageSystem}
                        onChange={(e) => setVoltageSystem(e.target.value as VoltageSystem)}
                      >
                        <MenuItem value="DC">DC</MenuItem>
                        <MenuItem value="AC1">AC 1-ph</MenuItem>
                        <MenuItem value="AC3">AC 3-ph</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField label="U (V)" value={voltage} onChange={(e) => setVoltage(e.target.value)} fullWidth />
                    <TextField label="I (A)" value={current} onChange={(e) => setCurrent(e.target.value)} fullWidth />
                    {voltageSystem !== 'DC' && (
                      <TextField label="cos phi" value={cosPhi} onChange={(e) => setCosPhi(e.target.value)} fullWidth />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">{powerHelper}</Typography>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="missing-power">Zielgroesse</InputLabel>
                    <Select
                      labelId="missing-power"
                      label="Zielgroesse"
                      value={missingPower}
                      onChange={(e) => setMissingPower(e.target.value as MissingPowerField)}
                    >
                      <MenuItem value="power">Leistung (kW)</MenuItem>
                      <MenuItem value="flow">Durchfluss (m3/h)</MenuItem>
                      <MenuItem value="deltaT">Delta T (K)</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Leistung (kW)"
                      helperText="Bekannt oder als Zielgroesse"
                      value={power}
                      onChange={(e) => setPower(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Durchfluss (m3/h)"
                      helperText="z.B. Wasser Volumenstrom"
                      value={flow}
                      onChange={(e) => setFlow(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Delta T (K)"
                      helperText="Temperaturdifferenz"
                      value={deltaT}
                      onChange={(e) => setDeltaT(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">{powerHelper}</Typography>
                </Stack>
              )}

              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                  border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">Berechnetes Ergebnis</Typography>
                <Typography variant="h4" fontWeight={700} color={tokens.colors.primary}>
                  {powerDisplay.value === null ? '-' : `${formatNumber(powerDisplay.value)} ${powerDisplay.unit}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ziel: {powerDisplay.label}
                </Typography>
              </Box>

              {detailSteps.length > 0 && (
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">Rechenweg Zielgroesse</Typography>
                    {detailSteps.slice(0, 2).map((line) => (
                      <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={getCardSx(darkMode)}>
          <CardContent>
            <Stack spacing={2.5}>
              <Typography variant="h6" fontWeight={700}>Verbrauch & Kosten</Typography>
              <FormControl fullWidth>
                <InputLabel id="missing-cost">Zielgroesse</InputLabel>
                <Select
                  labelId="missing-cost"
                  label="Zielgroesse"
                  value={missingCost}
                  onChange={(e) => setMissingCost(e.target.value as MissingCostField)}
                >
                  <MenuItem value="energy">Energie (kWh)</MenuItem>
                  <MenuItem value="hours">Betriebsstunden</MenuItem>
                  <MenuItem value="price">Preis (EUR/kWh)</MenuItem>
                  <MenuItem value="cost">Kosten (EUR)</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                <TextField label="Energie (kWh)" value={energy} onChange={(e) => setEnergy(e.target.value)} fullWidth />
                <TextField label="Betriebsstunden" value={hours} onChange={(e) => setHours(e.target.value)} fullWidth />
                <TextField label="Preis (EUR/kWh)" value={price} onChange={(e) => setPrice(e.target.value)} fullWidth />
                <TextField label="Kosten (EUR)" value={cost} onChange={(e) => setCost(e.target.value)} fullWidth />
              </Box>
              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                  border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">Berechnetes Ergebnis</Typography>
                <Typography variant="h4" fontWeight={700} color={tokens.colors.primary}>
                  {costResult === null ? '-' : formatNumber(costResult)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ergebnis bezieht sich auf die aktuell gewaehlte Zielgroesse{costPowerKw === null ? '.' : ` · Basisleistung ${formatNumber(costPowerKw, 2)} kW`}.
                </Typography>
              </Box>
              {detailSteps.length > 0 && (
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">Rechenweg Verbrauch/Kosten</Typography>
                    {detailSteps.slice(2).map((line) => (
                      <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                    ))}
                  </Stack>
                </Box>
              )}
              <Button variant="outlined" onClick={() => {
                if (missingCost === 'energy' && costResult !== null) setEnergy(formatNumber(costResult));
                if (missingCost === 'hours' && costResult !== null) setHours(formatNumber(costResult));
                if (missingCost === 'price' && costResult !== null) setPrice(formatNumber(costResult));
                if (missingCost === 'cost' && costResult !== null) setCost(formatNumber(costResult));
              }}>
                Ergebnis uebernehmen
              </Button>
              <Divider />
              <Typography variant="h6" fontWeight={700}>So verwenden</Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2" color="text.secondary">1) Modus waehlen: elektrisch oder thermisch (Waerme/Kaelte).</Typography>
                <Typography variant="body2" color="text.secondary">2) Zielgroesse in jedem Block festlegen (was soll berechnet werden).</Typography>
                <Typography variant="body2" color="text.secondary">3) Alle bekannten Werte eintragen und Ergebnis sowie Rechenweg pruefen.</Typography>
                <Typography variant="body2" color="text.secondary">4) Ergebnis auf Wunsch direkt in ein Eingabefeld uebernehmen.</Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
